import React, { type ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  Link as DSLink,
  Loader,
  Menu,
  SidePanel,
  Typography,
} from '@saviynt/design-system';
import { format, isValid } from 'date-fns';

import {
  normalizeRiskPriority,
  SodApplicationGroup,
  SodFieldSet,
  SodRiskPriorityChip,
  SodSeverityChip,
  SodTextFieldSet,
} from '../../../../shared/components';
import {
  AAG_SOD_ENTITLEMENT_REPORT,
  CF_NEU_STL,
  CF_SEC_BLD,
  EMPTY_FIELD_PLACEHOLDER,
  ENTITLEMENT_DETAILS_TABS,
  testIds,
  violationsEntitlement,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../../shared/hooks';
import { useEntitlementDetails } from '../../../Details/hooks';
import { entReportsActions } from '../../../EntitlementReports/data-store';
import { type EntitlementSidePanelRowContextState } from '../../data-layer/entitlementTypes';
import { type EntKebabOptions } from '../../data-store/entitlementMockData';
import { entitlementActions } from '../../data-store/entitlementSlice';

import './EntitlementSidePanel.css';

/**
 * SodEntitlementSidePanel Component
 *
 * Detailed sidepanel view for individual entitlement records with comprehensive information
 * display, navigation capabilities, and quick access to related data and actions for
 * entitlement management and analysis.
 *
 * @component
 * @returns {React.ReactElement} Sidepanel with detailed entitlement information including:
 *   - Entitlement metadata and descriptions
 *   - Quick navigation links to risks, users, violations
 *   - Severity indicators with color-coded chips
 *   - Application information with logos
 *   - Report generation and menu actions
 *
 * @example
 * <SodEntitlementSidePanel />
 *
 * @dependencies
 * - Redux: isSidePanelVisible, rowContext from state.aag.entitlement.sidepanel
 * - Hooks: useInvertedLocalize, useNavigateTo
 * - Components: SidePanel, SodApplicationGroup, SodFieldSet, SodSeverityChip
 */
function SodEntitlementSidePanel(): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const initialKebabOptionsWithIcons: EntKebabOptions[] = [
    {
      label: l('View Details'),
      value: ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY,
      icon: 'ArrowRight',
    },
  ];
  const {
    entitlement: {
      sidepanel: { isSidePanelVisible, rowContext },
    },
    details: {
      entitlementSummary: { loading: isLoading },
      entitlementDetails: { data: entitlementDetailsData },
    },
    global: { globalDateFormat },
  } = useTypedSelector((state) => state.aag);
  const sidepanelRef = useRef(null);
  const { navigateToDetails, navigateToUrl } = useNavigateTo();

  const [showContent, setShowContent] = useState(false);

  // Only fetch entitlement details when sidepanel is visible
  useEntitlementDetails();

  useEffect(() => {
    // Only show content when side panel is visible and data is loaded
    setShowContent(!isLoading && !!entitlementDetailsData);
  }, [isSidePanelVisible, isLoading, entitlementDetailsData]);

  const normalizedRiskPriority = normalizeRiskPriority(
    entitlementDetailsData?.riskObjects[0]?.riskPriority
  );
  const riskFieldValue = normalizedRiskPriority ? (
    <SodRiskPriorityChip
      priority={normalizedRiskPriority}
      riskScore={null}
      isPrefixInterpoint={false}
      isOnlyLabel
    />
  ) : (
    <Typography kind='body2' color={CF_SEC_BLD}>
      {EMPTY_FIELD_PLACEHOLDER}
    </Typography>
  );

  const handleSidePanelClose = () =>
    dispatch(entitlementActions.setIsSidePanelVisible(false));

  const handleSourceChange = (
    sourceType: string,
    activeTab: string,
    listItem: EntitlementSidePanelRowContextState
  ) => {
    navigateToDetails({
      key: String(listItem.entitlementValueKey),
      activeTab,
      isEntitlementRoute: true,
      navigation: {
        source: sourceType,
        title: listItem.entitlementDisplayName ?? '',
      },
    });
  };

  const sodSidePanelLayout = (
    selectedRow: EntitlementSidePanelRowContextState
  ) => (
    <>
      {/* 3 Col Link Grid */}
      <Box className='SodEntitlementSidePanel-detailLinks'>
        <Box className='SodEntitlementSidePanel-detailLinkBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Inherent Risks')}
          </Typography>
          <DSLink
            kind='button'
            onClick={() => {
              handleSourceChange(
                violationsEntitlement,
                ENTITLEMENT_DETAILS_TABS.TAB_RISKS,
                selectedRow
              );
            }}
            text={String(selectedRow.riskCount)}
            size='large'
            prefixIconKind='CriticalHighRiskHexagon'
            suffixIconKind='ArrowRight'
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailLinkBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Users')}
          </Typography>
          <DSLink
            kind='button'
            onClick={() => {
              handleSourceChange(
                violationsEntitlement,
                ENTITLEMENT_DETAILS_TABS.TAB_USERS,
                selectedRow
              );
            }}
            text={String(selectedRow.userCount)}
            size='large'
            prefixIconKind='User'
            suffixIconKind='ArrowRight'
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailLinkBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Violations')}
          </Typography>
          <DSLink
            kind='button'
            onClick={() => {
              handleSourceChange(
                violationsEntitlement,
                ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
                selectedRow
              );
            }}
            text={String(selectedRow.violationCount)}
            size='large'
            prefixIconKind='SOD'
            suffixIconKind='ArrowRight'
          />
        </Box>
      </Box>
      {/* Detail Chips */}
      <Box className='SodEntitlementSidePanel-detailChips'>
        <Box className='SodEntitlementSidePanel-rowItemsBlock'>
          <Box className='SodEntitlementSidePanel-detailChipBlock'>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('Application')}
            </Typography>
            <SodApplicationGroup
              label={selectedRow.endpointName ?? '-'}
              firstLogo={selectedRow.endPointLogo}
            />
          </Box>
          <Box className='SodEntitlementSidePanel-detailChipBlock'>
            <SodTextFieldSet
              label={l('Entitlement Type')}
              value={selectedRow.entitlementTypeName}
              lineLimit={1}
            />
          </Box>
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <SodTextFieldSet
            label={l('Description')}
            lineLimit={2}
            value={selectedRow.entitlementDescription}
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <SodTextFieldSet
            label={l('Glossary')}
            lineLimit={2}
            value={entitlementDetailsData?.glossary}
          />
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <SodTextFieldSet
            label={l('Custom Property 1')}
            lineLimit={2}
            value={entitlementDetailsData?.customProperty1}
          />
        </Box>
        <Box className='SodEntitlementSidePanel-chipsColumnList'>
          <Box className='SodEntitlementSidePanel-detailChipBlock'>
            <SodFieldSet
              label={l('Sox Critical')}
              value={
                <SodSeverityChip
                  text={selectedRow.entitlementSoxSeverity ?? 'None'}
                />
              }
            />
          </Box>
          <Box className='SodEntitlementSidePanel-detailChipBlock'>
            <SodFieldSet
              label={l('Sys Critical')}
              value={
                <SodSeverityChip
                  text={selectedRow.entitlementSysSeverity ?? 'None'}
                />
              }
            />
          </Box>
          <Box className='SodEntitlementSidePanel-detailChipBlock'>
            <SodFieldSet label={l('Risk')} value={riskFieldValue} />
          </Box>
          <Box className='SodEntitlementSidePanel-detailChipBlock'>
            <SodFieldSet
              label={l('Privileged')}
              value={
                <SodSeverityChip
                  text={selectedRow.entitlementPrivilegeSeverity ?? 'None'}
                />
              }
            />
          </Box>
        </Box>
        <Box className='SodEntitlementSidePanel-detailChipBlock'>
          <SodTextFieldSet
            label={l('Update Date')}
            value={
              selectedRow.lastUpdated && isValid(selectedRow.lastUpdated)
                ? format(new Date(selectedRow.lastUpdated), globalDateFormat)
                : selectedRow.lastUpdated
            }
          />
        </Box>
      </Box>
    </>
  );

  const sodSidePanelFooter = (
    selectedRow: EntitlementSidePanelRowContextState
  ) => (
    <FooterActionBar className='SodEntitlementSidePanel-footer'>
      <Button
        kind='outlined'
        leftIcon={<Icon kind='Report' />}
        type='button'
        size='medium'
        onClick={() => {
          // Set selected entitlement in Redux and navigate to reports
          dispatch(
            entReportsActions.setSelectedEntitlementValueKey(
              String(selectedRow.entitlementValueKey)
            )
          );
          dispatch(
            entReportsActions.setSelectedEntitlementName(
              String(selectedRow.entitlementDisplayName)
            )
          );
          navigateToUrl(AAG_SOD_ENTITLEMENT_REPORT);
        }}>
        {l('Generate Report')}
      </Button>
      <Menu
        options={initialKebabOptionsWithIcons}
        onChange={() => {
          handleSourceChange(
            violationsEntitlement,
            ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY,
            selectedRow
          );
        }}
        trigger={
          <ButtonIcon
            size='medium'
            kind='ghost'
            ariaLabel={l('More actions')}
            icon={<Icon kind='MoreVertical' />}
          />
        }
        placement='top-end'
      />
    </FooterActionBar>
  );

  return (
    <Box className='SodEntitlementSidePanel-sidePanelWrapper'>
      <SidePanel
        ref={sidepanelRef}
        isOpen={isSidePanelVisible}
        onClose={() => {
          handleSidePanelClose();
        }}
        header={{
          title: rowContext.current.entitlementDisplayName,
          description: l('Entitlement'),
          icon: 'Entitlement',
        }}
        appIconSize='medium'
        layout={
          showContent ? (
            sodSidePanelLayout(rowContext.current)
          ) : (
            <Box className='SodEntitlementSidePanel-sidePanelLoaderWrapper'>
              <Loader kind='dots' format='inline' color='brand' />
            </Box>
          )
        }
        footer={sodSidePanelFooter(rowContext.current)}
        isSidePanelNavigatingList
        isPreviousDisabled
        isNextDisabled
        isMobileWidth={false}
        isTabletWidth={false}
        className='SodEntitlementSidePanel-sidePanel'
        classNames={{
          layoutClassName: 'SodEntitlementSidePanel-sidePanelLayout',
          headerClassName: 'SodEntitlementSidePanel-sidePanelHeader',
          innerContentClassName:
            'SodEntitlementSidePanel-sidePanelInnerContent',
          footerClassName: 'SodEntitlementSidePanel-sidePanelFooter',
        }}
        dataTestId={testIds.SOD_ENTITLEMENT_TAB_SIDE_PANEL}
      />
    </Box>
  );
}

export default SodEntitlementSidePanel;
